import React, { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import HttpService from '../services/httpService';
import { setSnackInfo } from '../redux/actions/appAction';

const MAIN_HEADER_BG = '#1e3a8a';
const MAIN_HEADER_FG = '#ffffff';
const BORDER_LIGHT = '#e2e8f0';
const CELL_BORDER = `1px solid ${BORDER_LIGHT}`;
const ROW_STRIPE_A = '#ffffff';
const ROW_STRIPE_B = '#f8fafc';
const HIGHLIGHT_MEASURE_BG = '#dbeafe';
const TEXT_PRIMARY = '#0f172a';
const DIM_WIDTHS = [116, 160];

function isGrandTotalRow(row, dimensionCols) {
  if (!row) return false;
  if (row.rowType === 'grandTotal') return true;
  if (String(row.city ?? '').toLowerCase() === 'grand total') return true;
  const firstKey = dimensionCols[0]?.key;
  if (firstKey && String(row[firstKey] ?? '').toLowerCase() === 'grand total') return true;
  return false;
}

function formatDimensionCell(row, col, isGrand) {
  if (!row) return '-';
  const v = row[col.key];
  if (isGrand && col.key === 'ec' && (v === undefined || v === null || String(v).trim() === '')) {
    return '—';
  }
  if (v !== undefined && v !== null && String(v).trim() !== '') return v;
  if (v === '' || (v != null && String(v).trim() === '')) return '—';
  return '-';
}

function HrRacksheetSummaryByEC(props) {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const response = await HttpService.getRosterSummaryByEC();
      if (response?.data?.success === false) {
        props.setSnackInfo(
          response?.data?.message || 'Failed to load headcount summary by EC',
          'error'
        );
        setSummaryData(null);
        return;
      }
      setSummaryData(response?.data?.data ?? response?.data ?? null);
    } catch (error) {
      console.error('Error loading roster summary by EC:', error);
      props.setSnackInfo(
        error?.response?.data?.message || 'Error loading headcount summary by EC',
        'error'
      );
      setSummaryData(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = Array.isArray(summaryData?.columns) ? summaryData.columns : [];
  const valueKeys = Array.isArray(summaryData?.valueKeys) ? summaryData.valueKeys : [];
  const rawRows = Array.isArray(summaryData?.rows) ? summaryData.rows : [];
  const grandTotal = summaryData?.grandTotal;

  const dimensionCols = columns.filter((c) => c.type === 'dimension');
  const measureCols = columns.filter((c) => c.type === 'measure');

  const sortedRows = useMemo(() => {
    const cols = Array.isArray(summaryData?.columns) ? summaryData.columns : [];
    const dims = cols.filter((c) => c.type === 'dimension');
    const keys = dims.map((c) => c.key);
    const rows = Array.isArray(summaryData?.rows) ? summaryData.rows : [];
    if (!rows.length || !keys.length) return [...rows];
    return [...rows].sort((a, b) => {
      for (const key of keys) {
        const cmp = String(a[key] ?? '')
          .toLowerCase()
          .localeCompare(String(b[key] ?? '').toLowerCase(), undefined, { sensitivity: 'base' });
        if (cmp !== 0) return cmp;
      }
      return 0;
    });
  }, [summaryData]);

  const valueAtIndex = (row, idx) => {
    if (!row) return '-';
    if (Array.isArray(row.values) && row.values[idx] !== undefined && row.values[idx] !== null) {
      return row.values[idx];
    }
    const key = valueKeys[idx];
    if (key && row.counts && row.counts[key] !== undefined && row.counts[key] !== null) {
      return row.counts[key];
    }
    return '-';
  };

  const headerCellBase = {
    backgroundColor: MAIN_HEADER_BG,
    color: MAIN_HEADER_FG,
    fontWeight: 700,
    border: CELL_BORDER,
    padding: '12px 10px',
  };

  const isHighlightMeasure = (col) =>
    col.key === 'totalFilled' || col.key === 'totalActive';

  const renderTable = () => {
    if (!summaryData || measureCols.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          No summary data available.
        </Typography>
      );
    }

    const allRows = [...sortedRows];
    if (grandTotal && (grandTotal.values || grandTotal.counts)) {
      allRows.push(grandTotal);
    }

    let headerLeft = 0;
    const dimensionHeaders = dimensionCols.map((col, i) => {
      const w = DIM_WIDTHS[i] ?? 130;
      const left = headerLeft;
      headerLeft += w;
      return (
        <TableCell
          key={col.key}
          style={{
            ...headerCellBase,
            textAlign: 'left',
            minWidth: w,
            width: w,
            position: 'sticky',
            left,
            zIndex: 8 - i,
            boxShadow: '2px 0 4px rgba(15,23,42,0.06)',
          }}
        >
          {col.label}
        </TableCell>
      );
    });

    return (
      <TableContainer
        component={Paper}
        elevation={0}
        style={{
          maxWidth: '100%',
          overflowX: 'auto',
          border: CELL_BORDER,
          borderRadius: 0,
        }}
      >
        <Table size="small" stickyHeader style={{ borderCollapse: 'collapse' }}>
          <TableHead>
            <TableRow>
              {dimensionHeaders}
              {measureCols.map((col) => (
                <TableCell
                  key={col.key}
                  align="center"
                  style={{
                    ...headerCellBase,
                    minWidth: 84,
                    whiteSpace: 'nowrap',
                    zIndex: 3,
                  }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {allRows.map((row, idx) => {
              const isGrand = isGrandTotalRow(row, dimensionCols);
              const stripeBg = idx % 2 === 0 ? ROW_STRIPE_A : ROW_STRIPE_B;
              const rowBg = isGrand ? MAIN_HEADER_BG : stripeBg;
              const rowFg = isGrand ? MAIN_HEADER_FG : TEXT_PRIMARY;

              let bodyLeft = 0;
              const dimensionCells = dimensionCols.map((col, di) => {
                const w = DIM_WIDTHS[di] ?? 130;
                const left = bodyLeft;
                bodyLeft += w;
                return (
                  <TableCell
                    key={col.key}
                    style={{
                      position: 'sticky',
                      left,
                      zIndex: 4 - di,
                      backgroundColor: rowBg,
                      color: rowFg,
                      fontWeight: 700,
                      border: CELL_BORDER,
                      padding: '10px 12px',
                      textAlign: 'left',
                      minWidth: w,
                      width: w,
                      boxShadow: isGrand ? undefined : '2px 0 4px rgba(15,23,42,0.05)',
                    }}
                  >
                    {formatDimensionCell(row, col, isGrand)}
                  </TableCell>
                );
              });

              const rowKey = row.experienceCenterId || `${row.city}-${row.ec}-${idx}`;

              return (
                <TableRow key={String(rowKey)}>
                  {dimensionCells}
                  {measureCols.map((col) => {
                    const vkIdx = valueKeys.indexOf(col.key);
                    const mIdx = vkIdx >= 0 ? vkIdx : measureCols.indexOf(col);
                    const highlight = isHighlightMeasure(col);
                    const cellBg = isGrand
                      ? MAIN_HEADER_BG
                      : highlight
                        ? HIGHLIGHT_MEASURE_BG
                        : rowBg;
                    return (
                      <TableCell
                        key={col.key}
                        align="center"
                        style={{
                          backgroundColor: cellBg,
                          color: rowFg,
                          fontWeight: isGrand ? 700 : 400,
                          border: CELL_BORDER,
                          padding: '10px 8px',
                        }}
                      >
                        {valueAtIndex(row, mIdx)}
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const pageTitle = summaryData?.title || 'Headcount Summary — by EC';
  const ecCount = summaryData?.experienceCenterCount;

  return (
    <Box p={2}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        style={{ gap: 12 }}
        mb={1}
      >
        <Box>
          <Typography variant="h5" style={{ fontWeight: 700, color: TEXT_PRIMARY }}>
            {pageTitle}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          color="primary"
          disabled={loading}
          onClick={() => loadSummary()}
          style={{ borderColor: '#F47E46', color: '#F47E46' }}
        >
          {loading ? 'Loading…' : 'Refresh'}
        </Button>
      </Box>
      {renderTable()}
    </Box>
  );
}

export default connect(null, { setSnackInfo })(HrRacksheetSummaryByEC);
